import React, {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { css } from "styled-components";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import CommonButton from "components/common/ui/CommonButton";
import ColoredCircularProgress from "components/common/ui/ColorCircularProgress";
import SpacedTextInput, {
  SpacedAutocompleteInput,
  SpacedDatePicker,
} from "components/common/ui/SpacedTextInput";
import YesNoSwitch from "components/common/ui/YesNoSwitch";
import { usePromptSnackBar } from "components/common/ui/PromptSnackBarProvider";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import menuLoopDetailSelector from "store/selectors/menu/menuLoopDetailSelector";
import userGroupOptionsSelector from "store/selectors/userGroup/userGroupOptionSelector";
import { menuLoopActions } from "store/slices/menu/menuLoopDetailSlice";
import { DisplayMenuLoop } from "models/menu/menuLoop";

interface CopyMenuLoopDialogProps {
  open: boolean;
  onClose: () => void;
  editingMenuLoop: DisplayMenuLoop;
  menuLoopId: string | undefined;
}

const CopyMenuLoopDialog: FC<CopyMenuLoopDialogProps> = ({
  open,
  onClose,
  editingMenuLoop,
  menuLoopId,
}) => {
  const promptSnackBar = usePromptSnackBar();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isCopying = useAppSelector(menuLoopDetailSelector.isCopying);
  const siteOptionIDs = useAppSelector(userGroupOptionsSelector.siteOptionIDs);
  const userGroupOptions = useAppSelector(
    userGroupOptionsSelector.userGroupOptions,
  );

  const [newName, setNewName] = useState("");
  const [targetSiteId, setTargetSiteId] = useState<number | null>(null);
  const [newStartDate, setNewStartDate] = useState<Dayjs | null>(null);
  const [newEndDate, setNewEndDate] = useState<Dayjs | null>(null);
  const [copySpecialDates, setCopySpecialDates] = useState(false);
  const [copyMenuTemplates, setCopyMenuTemplates] = useState(true);

  useEffect(() => {
    if (open && editingMenuLoop) {
      setNewName(`${editingMenuLoop.name} (Copy)`);
      setTargetSiteId(null);
      setNewStartDate(
        editingMenuLoop.start_date ? dayjs(editingMenuLoop.start_date) : null,
      );
      setNewEndDate(
        editingMenuLoop.end_date ? dayjs(editingMenuLoop.end_date) : null,
      );
      setCopySpecialDates(false);
      setCopyMenuTemplates(true);
    }
  }, [open, editingMenuLoop]);

  const handleClose = useCallback(() => {
    if (!isCopying) {
      onClose();
    }
  }, [isCopying, onClose]);

  const isDateRangeValid =
    !!newStartDate?.isValid() &&
    !!newEndDate?.isValid() &&
    !newEndDate.isBefore(newStartDate, "day");

  const handleConfirm = useCallback(() => {
    if (
      editingMenuLoop &&
      newName.trim() &&
      targetSiteId &&
      menuLoopId &&
      isDateRangeValid
    ) {
      dispatch(
        menuLoopActions.copyMenuLoop({
          menuLoopId: Number(menuLoopId),
          requestBody: {
            new_name: newName.trim(),
            site_user_group_id: targetSiteId,
            skip_copy_special_dates: !copySpecialDates,
            copy_menu_templates: copyMenuTemplates,
            start_date: newStartDate!.format("YYYY-MM-DD"),
            end_date: newEndDate!.format("YYYY-MM-DD"),
          },
        }),
      )
        .unwrap()
        .then((message) => {
          promptSnackBar.setPromptProps({
            isOpen: true,
            message,
            severity: "success",
            autoHeightDuration: 5000,
          });
          onClose();
          navigate(-1);
        })
        .catch(() => {});
    }
  }, [
    editingMenuLoop,
    newName,
    targetSiteId,
    copySpecialDates,
    copyMenuTemplates,
    newStartDate,
    newEndDate,
    isDateRangeValid,
    menuLoopId,
    dispatch,
    promptSnackBar,
    onClose,
    navigate,
  ]);

  const handleTargetSiteChange = useCallback(
    (_: SyntheticEvent, newValue: number | null) => {
      setTargetSiteId(newValue);
    },
    [],
  );

  const availableSiteOptions = editingMenuLoop?.user_group_id
    ? siteOptionIDs.filter((siteId) => siteId !== editingMenuLoop.user_group_id)
    : siteOptionIDs;

  const isConfirmDisabled =
    !(newName.trim() && targetSiteId && menuLoopId && isDateRangeValid) ||
    isCopying;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isCopying}
    >
      <DialogTitle>Copy Menu Loop</DialogTitle>
      <Box
        sx={{
          px: 3,
          pt: 1,
          pb: 1,
          border: 1,
          borderColor: "warning.main",
          borderRadius: 1,
          mx: 3,
          mb: 1,
        }}
      >
        <Box display="flex" alignItems="center">
          <WarningIcon
            sx={{ fontSize: "1.5rem", color: "warning.main", mr: 0.5 }}
          />
          <Typography variant="body2" color="text.secondary">
            Unsaved changes won&apos;t be copied
          </Typography>
        </Box>
      </Box>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <SpacedTextInput
                  fullWidth
                  label="New Menu Loop Name"
                  placeholder="Enter the name for the copied menu loop..."
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <SpacedAutocompleteInput
                  fullWidth
                  label="Target Site"
                  options={availableSiteOptions}
                  getOptionLabel={(siteID) =>
                    userGroupOptions[siteID]?.group_name ?? `Site ID: ${siteID}`
                  }
                  value={targetSiteId}
                  onChange={handleTargetSiteChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SpacedDatePicker
                  label="New Repeat Start Date"
                  value={newStartDate}
                  onChange={(value) => setNewStartDate(value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SpacedDatePicker
                  label="New Repeat Until Date"
                  value={newEndDate}
                  onChange={(value) => setNewEndDate(value)}
                  slotProps={{
                    textField: {
                      helperText:
                        newStartDate?.isValid() &&
                        newEndDate?.isValid() &&
                        newEndDate.isBefore(newStartDate, "day")
                          ? "Shouldn't prior to start date."
                          : "",
                      error:
                        !!newStartDate?.isValid() &&
                        !!newEndDate?.isValid() &&
                        newEndDate.isBefore(newStartDate, "day"),
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <YesNoSwitch
                      checked={copyMenuTemplates}
                      onChange={(e) => setCopyMenuTemplates(e.target.checked)}
                    />
                  }
                  label="Copy Menu Templates"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <YesNoSwitch
                      checked={copySpecialDates}
                      onChange={(e) => setCopySpecialDates(e.target.checked)}
                    />
                  }
                  label="Copy special date events"
                />
              </Grid>
            </Grid>
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Box display="flex" alignItems="center" gap={1}>
          {isCopying && (
            <ColoredCircularProgress
              customColor={css`rgb(200, 200, 200)`}
              size={20}
            />
          )}
          <CommonButton
            text="Cancel"
            onClick={handleClose}
            disabled={isCopying}
          />
          <CommonButton
            text="Confirm"
            positive={!isConfirmDisabled}
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CopyMenuLoopDialog;
